import dayjs from "dayjs";

export type EventCategory = {
  id: string;
  label: string;
  emoji: string;
  types?: string[];
  datePreset?: "today" | "weekend";
};

export const EVENT_CATEGORIES: EventCategory[] = [
  // Date presets
  { id: "today",   label: "Today",        emoji: "📅", datePreset: "today" },
  { id: "weekend", label: "This weekend",  emoji: "🗓️", datePreset: "weekend" },
  // Community — "queer" is the umbrella tag the importer applies to mixed-crowd
  // events, so it surfaces under BOTH Gay and Sapphic. Events tagged only "gay"
  // or only "sapphic" stay in their own lane (queer-tagged ones appear in both).
  { id: "gay",     label: "Gay",           emoji: "💙", types: ["gay", "queer"] },
  { id: "sapphic", label: "Sapphic",       emoji: "🌸", types: ["sapphic", "queer"] },
  { id: "trans",   label: "Trans",         emoji: "🏳️‍⚧️", types: ["trans"] },
  { id: "bears",   label: "Bears",         emoji: "🐻", types: ["bear"] },
  // Food & drink
  { id: "dinner",  label: "Dinner",        emoji: "🍽️", types: ["dinner"] },
  { id: "brunch",  label: "Brunch",        emoji: "🍳", types: ["brunch"] },
  { id: "drinks",  label: "Drinks",        emoji: "🍸", types: ["drinks", "happy-hour", "wine-tasting", "cocktail-making", "coffee"] },
  { id: "nightlife", label: "Nightlife",   emoji: "🪩", types: ["club-night", "nightclub", "party", "rave", "white-party", "pool-party", "foam-party", "dance", "drag"] },
  // Activities
  { id: "arts",     label: "Arts & culture", emoji: "🎨", types: ["art", "theater", "museum", "photography", "crafts", "pottery", "improv", "comedy", "film-club", "movie", "poetry", "open-mic"] },
  { id: "games",    label: "Games",          emoji: "🎲", types: ["trivia", "games", "board-games", "card-games", "escape-room", "tabletop-rpg"] },
  { id: "music",    label: "Music & dance",  emoji: "🎵", types: ["concert", "music", "karaoke", "open-mic", "dance", "club-night", "rave"] },
  { id: "outdoors", label: "Outdoors",       emoji: "🌳", types: ["hike", "outdoor", "beach", "camping", "cycling", "running", "climbing"] },
  { id: "wellness", label: "Wellness",       emoji: "🧘", types: ["yoga", "fitness", "wellness", "meditation", "spa"] },
  { id: "learning", label: "Learning",       emoji: "✍️", types: ["workshop", "discussion", "book-club", "language-exchange", "networking", "speed-friending"] },
];

export function getCategoryDates(preset: "today" | "weekend"): { from: string; to: string } {
  const today = dayjs();
  if (preset === "today") {
    const d = today.format("YYYY-MM-DD");
    return { from: d, to: d };
  }
  // "This weekend" = Fri–Sun. If we're already in it (Fri/Sat/Sun) it starts
  // from TODAY — so on a Friday tonight's events (and the Tonight rail) still
  // show, instead of jumping past them to Saturday. Otherwise (Mon–Thu) it's
  // the coming Fri–Sun.
  const day = today.day(); // 0=Sun … 6=Sat
  const inWeekend = day === 5 || day === 6 || day === 0;
  const from = inWeekend ? today : today.add(5 - day, "day");
  const to = inWeekend
    ? today.add(day === 0 ? 0 : 7 - day, "day") // Fri→Sun, Sat→Sun, Sun→Sun
    : from.add(2, "day"); // Fri→Sun
  return {
    from: from.format("YYYY-MM-DD"),
    to: to.format("YYYY-MM-DD"),
  };
}
